import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";
import LocationPicker from "../../Components/LocationPicker";

const emptyForm = {
    hubType: "Hub",
    buildingNo: "",
    buildingName: "",
    street: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    latitude: "",
    longitude: "",
    inChargeId: "",
};

// Shared Add/Edit hub form, rendered as a Bootstrap modal it controls itself
// (bootstrap is attached to window in index.js) - mount it conditionally
// from the parent and it shows itself on mount, calling onClose once the
// hide transition finishes so there's exactly one way this ever closes.
const HubFormModal = ({ mode = "create", hub, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const [form, setForm] = useState(() => (mode === "edit" && hub ? {
        hubType: hub.hubType || "Hub",
        buildingNo: hub.buildingNo || "",
        buildingName: hub.buildingName || "",
        street: hub.street || "",
        area: hub.area || "",
        city: hub.city || "",
        state: hub.state || "",
        country: hub.country || "",
        pincode: hub.pincode ?? "",
        latitude: hub.latitude ?? "",
        longitude: hub.longitude ?? "",
        inChargeId: hub.inChargeId || "",
    } : emptyForm));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    // Coordinates from a map click/drag - fixed to 6 decimal places (~11cm
    // precision), plenty for a pickup hub and far tidier than the raw
    // float Google hands back.
    const handleLocationChange = ({ lat, lng }) => {
        setForm((prev) => ({ ...prev, latitude: lat.toFixed(6), longitude: lng.toFixed(6) }));
    };

    // Only fires from a deliberate address search-and-pick, never from a
    // plain map click/drag - so nudging an existing pin never silently
    // overwrites address text an admin already typed. Only non-empty
    // resolved fields are applied, for the same reason.
    const handleAddressResolved = (address) => {
        setForm((prev) => ({
            ...prev,
            ...(address.street ? { street: address.street } : {}),
            ...(address.area ? { area: address.area } : {}),
            ...(address.city ? { city: address.city } : {}),
            ...(address.state ? { state: address.state } : {}),
            ...(address.country ? { country: address.country } : {}),
            ...(address.pincode ? { pincode: address.pincode } : {}),
        }));
    };

    const validate = () => {
        const required = ["hubType", "buildingNo", "street", "area", "city", "state", "country", "pincode", "latitude", "longitude"];
        for (const field of required) {
            if (form[field] === "" || form[field] === undefined || form[field] === null) {
                return `${field} is required`;
            }
        }
        const lat = Number(form.latitude);
        const lng = Number(form.longitude);
        if (Number.isNaN(lat) || lat < -90 || lat > 90) return "Latitude must be between -90 and 90";
        if (Number.isNaN(lng) || lng < -180 || lng > 180) return "Longitude must be between -180 and 180";
        if (Number.isNaN(Number(form.pincode))) return "Pincode must be a number";
        return "";
    };

    const onSubmit = async () => {
        const error = validate();
        if (error) {
            toast.error(error);
            return;
        }

        const payload = {
            hubType: form.hubType,
            buildingNo: form.buildingNo.trim(),
            ...(form.buildingName.trim() ? { buildingName: form.buildingName.trim() } : {}),
            street: form.street.trim(),
            area: form.area.trim(),
            city: form.city.trim(),
            state: form.state.trim(),
            country: form.country.trim(),
            pincode: Number(form.pincode),
            latitude: Number(form.latitude),
            longitude: Number(form.longitude),
            ...(form.inChargeId.trim() ? { inChargeId: form.inChargeId.trim() } : {}),
        };

        setSaving(true);
        const response = mode === "edit"
            ? await Handler({ method: "patch", url: `/admin/hub/${hub.hubId}`, data: payload })
            : await Handler({ method: "post", url: "/admin/hub", data: payload });

        if (response?.success) {
            toast.success(response?.data?.message || (mode === "edit" ? "Hub updated" : "Hub created"));
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save the hub"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{mode === "edit" ? "Edit Hub" : "Add Hub"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-12 col-md-4">
                                <label className="form-label">Type</label>
                                <select className="form-select" name="hubType" value={form.hubType} onChange={handleChange}>
                                    <option value="Hub">Hub</option>
                                    <option value="Host">Host</option>
                                </select>
                            </div>
                            <div className="col-12 col-md-8">
                                <label className="form-label">In-charge staff ID (optional)</label>
                                <input type="text" className="form-control" name="inChargeId" placeholder="e.g. STF0001" value={form.inChargeId} onChange={handleChange} />
                                <div className="form-text">Must be an active staff member's ID.</div>
                            </div>

                            <div className="col-12">
                                <label className="form-label">Location</label>
                                <LocationPicker
                                    latitude={form.latitude}
                                    longitude={form.longitude}
                                    onLocationChange={handleLocationChange}
                                    onAddressResolved={handleAddressResolved}
                                />
                            </div>

                            <div className="col-12 col-md-4">
                                <label className="form-label">Building No.</label>
                                <input type="text" className="form-control" name="buildingNo" value={form.buildingNo} onChange={handleChange} />
                            </div>
                            <div className="col-12 col-md-8">
                                <label className="form-label">Building name (optional)</label>
                                <input type="text" className="form-control" name="buildingName" value={form.buildingName} onChange={handleChange} />
                            </div>

                            <div className="col-12">
                                <label className="form-label">Street</label>
                                <input type="text" className="form-control" name="street" value={form.street} onChange={handleChange} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Area</label>
                                <input type="text" className="form-control" name="area" value={form.area} onChange={handleChange} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Pincode</label>
                                <input type="number" className="form-control" name="pincode" value={form.pincode} onChange={handleChange} />
                            </div>

                            <div className="col-12 col-md-4">
                                <label className="form-label">City</label>
                                <input type="text" className="form-control" name="city" value={form.city} onChange={handleChange} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">State</label>
                                <input type="text" className="form-control" name="state" value={form.state} onChange={handleChange} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Country</label>
                                <input type="text" className="form-control" name="country" value={form.country} onChange={handleChange} />
                            </div>

                            <div className="col-12 col-md-6">
                                <label className="form-label">Latitude</label>
                                <input type="number" step="any" className="form-control" name="latitude" value={form.latitude} onChange={handleChange} />
                                <div className="form-text">Set by the map above, or type it directly.</div>
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Longitude</label>
                                <input type="number" step="any" className="form-control" name="longitude" value={form.longitude} onChange={handleChange} />
                                <div className="form-text">Set by the map above, or type it directly.</div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Create hub"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HubFormModal;
